<script setup lang="ts">
/**
 * One entry of the sign-in family of screens: a dark inset kit well (`.k-well`)
 * with the typed text and an optional trailing control (the password eye). No
 * lead icon: the card was judged busy (2026-10-14), the placeholder names the
 * field. Owns the look of a free-text input on
 * those screens; the value and its rules belong to the caller.
 *
 * The label stays in the DOM for screen readers and for the Papers screen's
 * password change (`labelHidden` false); on the sign-in card the placeholder
 * carries the name.
 */

defineOptions({ inheritAttrs: false });

withDefaults(
  defineProps<{
    id: string;
    label: string;
    type?: string;
    labelHidden?: boolean;
    disabled?: boolean;
  }>(),
  { type: 'text', labelHidden: true, disabled: false },
);

const model = defineModel<string>({ required: true });
</script>

<template>
  <div class="field">
    <label :for="id" :class="labelHidden ? 'sr-only' : 'caption'">{{ label }}</label>
    <div class="k-well well" :class="{ off: disabled }">
      <input :id="id" v-model="model" :type="type" :disabled="disabled" v-bind="$attrs" />
      <slot name="trail" />
    </div>
  </div>
</template>

<style scoped>
.caption {
  display: block;
  margin: 0 0 4px 4px;
  font-family: var(--font-game-body);
  font-size: 0.8rem;
  font-weight: 500;
  color: #b8c7d6;
}

.well {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 52px;
  padding: 0 6px 0 14px;
  transition: box-shadow var(--dur) var(--ease);
}

/* Focus is a gold rim round the well: seen on a phone as well as with a keyboard. */
.well:focus-within {
  box-shadow:
    inset 0 2px 6px rgba(0, 0, 0, 0.55),
    0 0 0 2px var(--kit-gold);
}

@media (max-height: 520px) {
  .well {
    min-height: 46px;
  }
}

.off {
  opacity: 0.6;
}

input {
  flex: 1;
  min-width: 0;
  height: 100%;
  min-height: 48px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  /* 16 px or more: below that iOS Safari zooms the page when the field is focused. */
  font-family: var(--font-game-body);
  font-size: 1rem;
  font-weight: 500;
  caret-color: var(--kit-gold);
}

input::placeholder {
  color: #7f93a7;
  opacity: 1;
}

input:focus {
  outline: none;
}

/* Autofill paints its own pale well; keep the entry on the dark steel. */
input:-webkit-autofill {
  -webkit-text-fill-color: #fff;
  -webkit-box-shadow: 0 0 0 100px #0e1722 inset;
  box-shadow: 0 0 0 100px #0e1722 inset;
}
</style>
